// 黑哥们事件弹窗
import { useEffect, useState } from 'react';
import { Modal, Space, Typography, Tag, Button } from 'antd';
import {
  AlertOutlined,
  ThunderboltFilled,
  CloseCircleFilled,
  CheckCircleFilled,
} from '@ant-design/icons';
import type { HeigeEvent } from '../../shared/types';
import type { HeigeTriggerResult } from '../../shared/ipc';
const { Text } = Typography;
interface Props {
  data: { event: HeigeEvent;
 result: HeigeTriggerResult } | null;
  onClose: () => void;
}
const severityColor: Record<number, string> = {
  1: '#52c41a',
  2: '#1677ff',
  3: '#faad14',
  4: '#ff7a45',
  5: '#ff4d4f',
};
const severityLabel: Record<number, string> = {
  1: '轻微',
  2: '较轻',
  3: '中等',
  4: '较重',
  5: '严重',
};
export default function HeigeNotification({ data, onClose }: Props) {
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    if (data) {
      setVisible(true);
    }
  }, [data]);
  if (!data) return null;
  const { event, result } = data;
  const totalLoss = result.affected.reduce((s, a) => s + (a.insurance ? 0 : a.amount), 0);
  const guardBlocked = result.blockByGuard;
  const insuranceUsed = result.affected.some((a) => a.insurance);
  return (
    <Modal
      open={visible}
      onCancel={() => {
        setVisible(false);
        onClose();
      }}
      footer={[
        <Button
          key="ok"
          type="primary"
          danger
          onClick={() => {
            setVisible(false);
            onClose();
          }}
        >
          {' '}
          认了 🏴‍☠️{' '}
        </Button>,
      ]}
      width={520}
      centered
      closable={false}
      maskClosable={false}
      styles={{
        body: { background: '#1a0f0f', padding: 24 },
        header: { background: '#1a0f0f', borderBottom: '1px solid #ff4d4f' },
        content: { background: '#1a0f0f' },
        footer: { background: '#1a0f0f', borderTop: '1px solid #30363d' },
      }}
      title={
        <Space>
          {' '}
          <AlertOutlined style={{ color: '#ff4d4f', fontSize: 18 }} />{' '}
          <span style={{ color: '#ff4d4f', fontWeight: 700 }}>黑哥们来袭！</span>{' '}
        </Space>
      }
    >
      {' '}
      <Space direction="vertical" style={{ width: '100%' }} size={14}>
        {' '}
        {/* 事件描述 */}{' '}
        <div
          style={{
            background: '#0d1117',
            border: `1px solid ${severityColor[event.severity]}`,
            borderRadius: 6,
            padding: 12,
          }}
        >
          {' '}
          <Space direction="vertical" size={6} style={{ width: '100%' }}>
            {' '}
            <Space>
              {' '}
              <ThunderboltFilled style={{ color: severityColor[event.severity] }} />{' '}
              <Text strong style={{ fontSize: 14 }}>
                {' '}
                {event.label}{' '}
              </Text>{' '}
            </Space>{' '}
            <Space>
              {' '}
              <Tag color={event.severity >= 4 ? 'red' : 'orange'}>
                {' '}
                严重度: {severityLabel[event.severity]} ({event.severity}/5){' '}
              </Tag>{' '}
              <Tag>{new Date(event.timestamp).toLocaleTimeString('zh-CN')}</Tag>{' '}
            </Space>{' '}
          </Space>{' '}
        </div>{' '}
        {/* 保安拦截 */}{' '}
        {guardBlocked && (
          <div
            style={{
              background: 'rgba(82, 196, 26, 0.1)',
              border: '1px solid #52c41a',
              borderRadius: 6,
              padding: 12,
            }}
          >
            {' '}
            <Space>
              {' '}
              <CheckCircleFilled style={{ color: '#52c41a', fontSize: 18 }} />{' '}
              <Text style={{ color: '#52c41a' }}>
                {' '}
                <strong>保安出击！</strong>本次偷窃已被 100% 拦截。{' '}
              </Text>{' '}
            </Space>{' '}
          </div>
        )}{' '}
        {/* 保险保护 */}{' '}
        {insuranceUsed && !guardBlocked && (
          <div
            style={{
              background: 'rgba(250, 173, 20, 0.1)',
              border: '1px solid #faad14',
              borderRadius: 6,
              padding: 12,
            }}
          >
            {' '}
            <Space>
              {' '}
              <CheckCircleFilled style={{ color: '#faad14', fontSize: 18 }} />{' '}
              <Text style={{ color: '#faad14' }}>
                {' '}
                <strong>保险已赔付：</strong>本次共挽回 ¥
                {result.totalPayout.toLocaleString(undefined, { maximumFractionDigits: 0 })}{' '}
              </Text>{' '}
            </Space>{' '}
          </div>
        )}{' '}
        {/* 被偷清单 */}{' '}
        {!guardBlocked && result.affected.length > 0 && (
          <div
            style={{
              background: 'rgba(255, 77, 79, 0.08)',
              border: '1px solid #ff4d4f',
              borderRadius: 6,
              padding: 12,
            }}
          >
            {' '}
            <Space direction="vertical" size={6} style={{ width: '100%' }}>
              {' '}
              <Text strong style={{ color: '#ff4d4f' }}>
                {' '}
                被光顾的持仓：{' '}
              </Text>{' '}
              {result.affected.map((a, i) => (
                <Space key={i} style={{ width: '100%', justifyContent: 'space-between' }}>
                  {' '}
                  <Space>
                    {' '}
                    <CloseCircleFilled
                      style={{ color: a.insurance ? '#52c41a' : '#ff4d4f' }}
                    />{' '}
                    <span>{a.name}</span>{' '}
                    <span style={{ color: '#8b949e', fontSize: 11 }}>({a.shares} 股)</span>{' '}
                    {a.insurance && <Tag color="success">保险</Tag>}{' '}
                  </Space>{' '}
                  <span style={{ color: a.insurance ? '#52c41a' : '#ff4d4f', fontWeight: 600 }}>
                    {' '}
                    {a.insurance
                      ? '+0'
                      : `-¥${a.amount.toLocaleString(undefined, { maximumFractionDigits: 0 })}`}{' '}
                  </span>{' '}
                </Space>
              ))}{' '}
            </Space>{' '}
          </div>
        )}{' '}
        {/* 任务 2 奖励 */}{' '}
        {result.task2Reward > 0 && (
          <div
            style={{
              background: 'rgba(82, 196, 26, 0.1)',
              border: '1px solid #52c41a',
              borderRadius: 6,
              padding: 12,
            }}
          >
            {' '}
            <Space>
              {' '}
              <CheckCircleFilled style={{ color: '#52c41a' }} />{' '}
              <Text style={{ color: '#52c41a' }}>
                {' '}
                <strong>任务 2 结算：</strong>+¥{result.task2Reward.toLocaleString()}{' '}
              </Text>{' '}
            </Space>{' '}
          </div>
        )}{' '}
        {/* 总损失 */}{' '}
        {!guardBlocked && totalLoss > 0 && (
          <div style={{ textAlign: 'center', padding: 8 }}>
            {' '}
            <Text style={{ color: '#8b949e', fontSize: 12 }}>本次总损失</Text>{' '}
            <div
              style={{ fontSize: 28, fontWeight: 700, color: '#ff4d4f', fontFamily: 'monospace' }}
            >
              {' '}
              -¥{totalLoss.toLocaleString(undefined, { maximumFractionDigits: 0 })}{' '}
            </div>{' '}
          </div>
        )}{' '}
      </Space>{' '}
    </Modal>
  );
}
